import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { LayoutGrid, Eye, EyeOff, Shield, CheckCircle, Clock, BarChart, Loader2 } from "lucide-react";
import { authAPI } from "../../services/api";
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import axios from "axios";
import config from "../../config/config";

const API_URL = config.API_URL;


const Login = () => {
  const { login, candidateLogin, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState("login"); // Tab state (login/register)
  const [loginType, setLoginType] = useState("company"); // Sub-tab state (company/candidate)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [firstUserId, setFirstUserId] = useState(null);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  
  // Candidate login states
  const [candidateEmail, setCandidateEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(600); // 10 minutes in seconds
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [profiles, setProfiles] = useState([]); // Multiple candidate profiles
  const [showProfileSelection, setShowProfileSelection] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);

  // Check if redirected from candidate route
  useEffect(() => {
    if (location.state?.candidateLogin) {
      setActiveTab("login");
      setLoginType("candidate");
    }
  }, [location]);

  // Phone number regex
  const phoneRex = /^\(?\d{3}\)?[- ]?\d{3}[- ]?\d{4}$/;

  // Yup validation schema for registration
  const registrationValidationSchema = Yup.object({
    adminName: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters")
      .max(50, "Name must be less than 50 characters"),
    
    phone: Yup.string()
      .required("Phone Number is required")
      .matches(phoneRex, "Enter a Valid Phone Number"),
    
    adminEmail: Yup.string()
      .required("Email is required")
      .email("Enter a valid email address"),
    
    adminPassword: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Password must contain at least one uppercase letter, one lowercase letter, and one number"
      ),
    
    companyName: Yup.string()
      .required("Company Name is required")
      .min(2, "Company name must be at least 2 characters")
      .max(100, "Company name must be less than 100 characters"),
    
    companyUrl: Yup.string()
      .required("Company Url is required")
      .url("Enter a valid URL (e.g., https://example.com)"),
    
    address: Yup.string()
      .required("Company Address is required")
      .min(10, "Address must be at least 10 characters")
      .max(200, "Address must be less than 200 characters")
  });

  // Initial form values for registration
  const registrationInitialValues = {
    adminName: "",
    phone: "",
    adminEmail: "",
    adminPassword: "",
    companyName: "",
    companyUrl: "",
    address: "",
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: "Error",
        description: "Please enter both email and password",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const user = await login(email, password); // Get full user object


      if (user.is_first_login) {
        setFirstUserId(user._id); // backend expects _id
        setShowPasswordReset(true);
        toast({
          title: "First-time login",
          description: "Please reset your password",
        });
        return;
      }


      const role = localStorage.getItem("role");
      if (role === "master_admin") {
        navigate("/master-admin");
      } else if (role === "company_super_admin") {
        navigate("/super-admin");
      } else if (role === "company_admin") {
        navigate("/admin");
      } else {
        navigate("/login");
      }

    } catch (error) {
      toast({
        title: "Login failed",
        description: error.response?.data?.message || "Invalid credentials",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (!newPassword) {
      toast({
        title: "Error",
        description: "Please enter a new password",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await authAPI.resetFirstTimePassword(firstUserId, newPassword);
      toast({
        title: "Success",
        description: "Password reset! Please login again.",
      });
      setShowPasswordReset(false);
      setPassword("");
      setNewPassword("");
      navigate("/login");
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Could not reset password",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleRegisterPasswordVisibility = () => {
    setShowRegisterPassword(!showRegisterPassword);
  };

  // OTP Timer Effect
  useEffect(() => {
    let interval;
    if (otpSent && otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            setCanResendOtp(true);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpSent, otpTimer]);

  // Format timer display (MM:SS)
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Registration form submission handler
  const handleRegistrationSubmit = async (values, { setSubmitting, setFieldError }) => {
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API_URL}/company/register`, values, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      toast({
        title: "Success",
        description: "Company registered successfully!",
      });
      setIsSubmitting(false);
      setActiveTab("login"); // Switch to login tab after successful registration
    } catch (error) {
      console.error("Registration error:", error);
      
      // Handle specific field errors from server
      if (error.response?.data?.fieldErrors) {
        Object.keys(error.response.data.fieldErrors).forEach(field => {
          setFieldError(field, error.response.data.fieldErrors[field]);
        });
      } else {
        toast({
          title: "Error",
          description: error.response?.data?.message || "Registration failed. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  // Candidate OTP request handler
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!candidateEmail) {
      toast({
        title: "Error",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const success = await candidateLogin(candidateEmail);
      if (success) {
        setOtpSent(true);
        setOtpTimer(600); // Reset to 10 minutes
        setCanResendOtp(false);
        toast({
          title: "Success",
          description: "Verification code sent to your email",
        });
      }
    } catch (error) {
      console.error("Error requesting OTP:", error);
      toast({
        title: "Error",
        description: "Failed to send verification code",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Resend OTP handler
  const handleResendOtp = async () => {
    setIsSubmitting(true);
    try {
      const success = await candidateLogin(candidateEmail);
      if (success) {
        setOtpTimer(600); // Reset to 10 minutes
        setCanResendOtp(false);
        setOtp(""); // Clear previous OTP
        toast({
          title: "Success",
          description: "New verification code sent to your email",
        });
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      toast({
        title: "Error",
        description: "Failed to resend verification code",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Candidate OTP verification handler
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      toast({
        title: "Error",
        description: "Please enter a valid 6-digit verification code",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await verifyOtp(candidateEmail, otp);
      
      // Check if profile selection is required
      if (result.requiresProfileSelection) {
        setProfiles(result.profiles);
        setShowProfileSelection(true);
        toast({
          title: "Multiple Profiles Found",
          description: "Please select your company profile to continue",
        });
      } else if (result.success) {
        navigate("/candidate");
      } else {
        setOtp("");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setOtp("");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle profile selection
  const handleProfileSelection = async (candidateId) => {
    setIsSubmitting(true);
    try {
      const result = await verifyOtp(candidateEmail, otp, candidateId);
      if (result.success) {
        navigate("/candidate");
      }
    } catch (error) {
      console.error("Error selecting profile:", error);
      toast({
        title: "Error",
        description: "Failed to login with selected profile",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="min-h-screen h-screen bg-white flex overflow-hidden">
      {/* Left side - Content Section with Blue/Purple Background - Fixed, No Scroll */}
      <div className="hidden lg:flex lg:w-[70%] bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 text-white p-12 flex-col justify-between overflow-hidden">
        {/* Logo and Title */}
        <div>
          <div className="flex items-center mb-8">
            <LayoutGrid className="h-10 w-10 text-white" />
            <div className="ml-3">
              <h1 className="text-2xl font-bold">AssessPro</h1>
              <p className="text-sm text-blue-100">Assessment Management System</p>
            </div>
          </div>

          <h2 className="text-4xl font-bold mb-6 leading-tight">
            Complete Assessment Management Solution
          </h2>
          <p className="text-lg text-blue-50 mb-12">
            Streamline your assessment process with our comprehensive platform
          </p>

          {/* Feature Cards */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <Shield className="h-8 w-8 mb-3 text-blue-200" />
              <h3 className="font-semibold mb-1">Secure & Reliable</h3>
              <p className="text-sm text-blue-100">Enterprise-grade security & encryption</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <Clock className="h-8 w-8 mb-3 text-blue-200" />
              <h3 className="font-semibold mb-1">Real-time Updates</h3>
              <p className="text-sm text-blue-100">Instant notifications & live tracking</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <CheckCircle className="h-8 w-8 mb-3 text-blue-200" />
              <h3 className="font-semibold mb-1">Easy Integration</h3>
              <p className="text-sm text-blue-100">Connect with existing tools & more</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <BarChart className="h-8 w-8 mb-3 text-blue-200" />
              <h3 className="font-semibold mb-1">Advanced Analytics</h3>
              <p className="text-sm text-blue-100">Comprehensive reporting dashboards</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-sm text-blue-100">
          © 2024 AssessPro. All rights reserved.
        </div>
      </div>

      {/* Right side - Login Form Section - Scrollable */}
      <div className="w-full lg:w-[30%] flex items-center justify-center p-8 bg-gray-50 overflow-y-auto">
        <div className="w-full max-w-md my-auto">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <LayoutGrid className="h-8 w-8 text-primary" />
            <h1 className="ml-2 text-2xl font-bold text-gray-900">AssessPro</h1>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            {/* Tabs Header */}
            <div className="flex border-b border-gray-200 mb-6">
              <button
                onClick={() => setActiveTab("login")}
                className={`flex-1 py-3 text-center font-medium transition-colors ${
                  activeTab === "login"
                    ? "text-primary border-b-2 border-primary"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Login
              </button>
              <button
                onClick={() => setActiveTab("register")}
                className={`flex-1 py-3 text-center font-medium transition-colors ${
                  activeTab === "register"
                    ? "text-primary border-b-2 border-primary"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Register
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === "login" ? (
              <>
                {/* Sub-tabs for Company Login and Candidate Login */}
                <div className="flex border-b border-gray-200 mb-6">
                  <button
                    onClick={() => {
                      setLoginType("company");
                      setOtpSent(false);
                      setCandidateEmail("");
                      setOtp("");
                    }}
                    className={`flex-1 py-2 text-center text-sm font-medium transition-colors ${
                      loginType === "company"
                        ? "text-primary border-b-2 border-primary"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Company Login
                  </button>
                  <button
                    onClick={() => {
                      setLoginType("candidate");
                      setShowPasswordReset(false);
                    }}
                    className={`flex-1 py-2 text-center text-sm font-medium transition-colors ${
                      loginType === "candidate"
                        ? "text-primary border-b-2 border-primary"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Candidate Login
                  </button>
                </div>

                {loginType === "company" ? (
                  <>
                    {/* Header */}
                    <div className="mb-8">
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        {showPasswordReset ? "Reset Password" : "Welcome Back"}
                      </h2>
                      <p className="text-gray-600">
                        {showPasswordReset
                          ? "Please set a new password to continue"
                          : "Sign in to access the admin dashboard"}
                      </p>
                    </div>

                    {/* Login Forms */}
                    {showPasswordReset ? (
                      <form onSubmit={handlePasswordUpdate} className="space-y-6">
                        <div className="space-y-2">
                          <Label htmlFor="newPassword" className="text-gray-700">New Password</Label>
                          <div className="relative">
                            <Input
                              id="newPassword"
                              type={showNewPassword ? "text" : "password"}
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              className="pr-10"
                              placeholder="Enter new password"
                              required
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                              onClick={toggleNewPasswordVisibility}
                            >
                              {showNewPassword ? (
                                <EyeOff className="h-4 w-4 text-gray-500" />
                              ) : (
                                <Eye className="h-4 w-4 text-gray-500" />
                              )}
                            </Button>
                          </div>
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full" 
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Updating..." : "Update Password"}
                        </Button>
                      </form>
                    ) : (
                      <form onSubmit={handleLogin} className="space-y-6">
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-gray-700">Email</Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="h-11"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="password" className="text-gray-700">Password</Label>
                          <div className="relative">
                            <Input
                              id="password"
                              type={showPassword ? "text" : "password"}
                              placeholder="Enter your password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              className="pr-10 h-11"
                              required
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                              onClick={togglePasswordVisibility}
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4 text-gray-500" />
                              ) : (
                                <Eye className="h-4 w-4 text-gray-500" />
                              )}
                            </Button>
                          </div>
                        </div>
                        <Button
                          type="submit"
                          className="w-full h-11 font-medium"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Signing in..." : "Sign In"}
                        </Button>
                      </form>
                    )}

                    {/* Footer Link */}
                    <div className="mt-6 text-center">
                      <button
                        onClick={() => navigate("/")}
                        className="text-primary hover:underline text-sm font-medium"
                      >
                        Go to Home
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Candidate Login */}
                    <div className="mb-8">
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        Candidate Access
                      </h2>
                      <p className="text-gray-600">
                        {!otpSent
                          ? "Enter your email to receive a verification code"
                          : `Enter the code sent to ${candidateEmail}`}
                      </p>
                    </div>

                    {!otpSent ? (
                      <form onSubmit={handleRequestOtp} className="space-y-6">
                        <div className="space-y-2">
                          <Label htmlFor="candidateEmail" className="text-gray-700">Email</Label>
                          <Input
                            id="candidateEmail"
                            type="email"
                            placeholder="your.email@example.com"
                            value={candidateEmail}
                            onChange={(e) => setCandidateEmail(e.target.value)}
                            className="h-11"
                            required
                          />
                        </div>
                        <Button
                          type="submit"
                          className="w-full h-11 font-medium"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Sending..." : "Get Verification Code"}
                        </Button>
                      </form>
                    ) : showProfileSelection ? (
                      <div className="space-y-6">
                        <div className="space-y-2">
                          <Label className="text-gray-700">Select Your Company Profile</Label>
                          <p className="text-sm text-gray-600">
                            Multiple profiles found for {candidateEmail}. Please select the company you want to access.
                          </p>
                        </div>

                        <div className="space-y-3">
                          {profiles.map((profile) => (
                            <button
                              key={profile.candidateId}
                              type="button"
                              onClick={() => handleProfileSelection(profile.candidateId)}
                              disabled={isSubmitting}
                              className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-semibold text-gray-900">{profile.name}</p>
                                  <p className="text-sm text-gray-600">{profile.company?.name || "Unknown Company"}</p>
                                </div>
                                <div className="text-primary">
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>

                        <Button
                          type="button"
                          variant="ghost"
                          className="w-full h-11 font-medium"
                          onClick={() => {
                            setShowProfileSelection(false);
                            setOtpSent(false);
                            setOtp("");
                            setProfiles([]);
                            setOtpTimer(600);
                            setCanResendOtp(false);
                          }}
                          disabled={isSubmitting}
                        >
                          Back to Login
                        </Button>
                      </div>
                    ) : (
                      <form onSubmit={handleVerifyOtp} className="space-y-6">
                        <div className="space-y-2">
                          <Label htmlFor="otp" className="text-gray-700">Verification Code</Label>
                          <Input
                            id="otp"
                            type="text"
                            placeholder="6-digit code"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            maxLength={6}
                            className="h-11 text-center text-lg"
                            required
                          />
                        </div>

                        {/* Buttons in horizontal layout */}
                        <div className="flex gap-3">
                          <Button
                            type="submit"
                            className="flex-1 h-11 font-medium"
                            disabled={isSubmitting || otpTimer === 0}
                          >
                            {isSubmitting ? "Verifying..." : "Verify & Login"}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="flex-1 h-11 font-medium"
                            onClick={handleResendOtp}
                            disabled={!canResendOtp || isSubmitting}
                          >
                            {isSubmitting ? "Sending..." : canResendOtp ? "Resend OTP" : `Resend OTP (${formatTimer(otpTimer)})`}
                          </Button>
                        </div>

                        <Button
                          type="button"
                          variant="ghost"
                          className="w-full h-11 font-medium"
                          onClick={() => {
                            setOtpSent(false);
                            setOtp("");
                            setOtpTimer(600);
                            setCanResendOtp(false);
                          }}
                        >
                          Back
                        </Button>
                      </form>
                    )}

                    {/* Footer Link */}
                    <div className="mt-6 text-center">
                      <button
                        onClick={() => navigate("/")}
                        className="text-primary hover:underline text-sm font-medium"
                      >
                        Go to Home
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                {/* Register Form Header */}
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Register Your Company</h2>
                  <p className="text-gray-600">Create a company account to start managing assessments</p>
                </div>

                {/* Register Form */}
                <Formik
                  initialValues={registrationInitialValues}
                  validationSchema={registrationValidationSchema}
                  onSubmit={handleRegistrationSubmit}
                >
                  {({ values, errors, touched, handleChange, handleBlur, isSubmitting: formikSubmitting }) => (
                    <Form className="space-y-4">
                      <p className="font-bold text-base">Personal Details</p>
                      
                      <div className="grid grid-cols-1 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="adminName">Name</Label>
                          <Field
                            as={Input}
                            id="adminName"
                            name="adminName"
                            placeholder="John Doe"
                            value={values.adminName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={errors.adminName && touched.adminName ? "border-red-500 focus:border-red-500" : ""}
                          />
                          <ErrorMessage name="adminName" component="p" className="text-sm text-red-500" />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone Number</Label>
                          <Field
                            as={Input}
                            id="phone"
                            name="phone"
                            placeholder="+1 234 567 8901"
                            value={values.phone}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={errors.phone && touched.phone ? "border-red-500 focus:border-red-500" : ""}
                          />
                          <ErrorMessage name="phone" component="p" className="text-sm text-red-500" />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="adminEmail">Email</Label>
                          <Field
                            as={Input}
                            id="adminEmail"
                            name="adminEmail"
                            type="email"
                            placeholder="admin@example.com"
                            value={values.adminEmail}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={errors.adminEmail && touched.adminEmail ? "border-red-500 focus:border-red-500" : ""}
                          />
                          <ErrorMessage name="adminEmail" component="p" className="text-sm text-red-500" />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="adminPassword">Password</Label>
                          <div className="relative">
                            <Input
                              id="adminPassword"
                              name="adminPassword"
                              type={showRegisterPassword ? "text" : "password"}
                              placeholder="********"
                              value={values.adminPassword}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`pr-10 ${errors.adminPassword && touched.adminPassword ? "border-red-500 focus:border-red-500" : ""}`}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                              onClick={toggleRegisterPasswordVisibility}
                            >
                              {showRegisterPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                          <ErrorMessage name="adminPassword" component="p" className="text-sm text-red-500" />
                        </div>
                      </div>

                      <p className="font-bold text-base">Company Details</p>
                      
                      <div className="grid grid-cols-1 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="companyName">Company Name</Label>
                          <Field
                            as={Input}
                            id="companyName"
                            name="companyName"
                            placeholder="ABC Corporation"
                            value={values.companyName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={errors.companyName && touched.companyName ? "border-red-500 focus:border-red-500" : ""}
                          />
                          <ErrorMessage name="companyName" component="p" className="text-sm text-red-500" />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="companyUrl">Company Website</Label>
                          <Field
                            as={Input}
                            id="companyUrl"
                            name="companyUrl"
                            placeholder="https://example.com"
                            value={values.companyUrl}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={errors.companyUrl && touched.companyUrl ? "border-red-500 focus:border-red-500" : ""}
                          />
                          <ErrorMessage name="companyUrl" component="p" className="text-sm text-red-500" />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="address">Company Address</Label>
                          <Field
                            as={Input}
                            id="address"
                            name="address"
                            placeholder="123 Main St, City, Country"
                            value={values.address}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={errors.address && touched.address ? "border-red-500 focus:border-red-500" : ""}
                          />
                          <ErrorMessage name="address" component="p" className="text-sm text-red-500" />
                        </div>
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full" 
                        disabled={isSubmitting || formikSubmitting}
                      >
                        {isSubmitting && <Loader2 className="animate-spin mr-2" />}
                        {isSubmitting ? "Registering..." : "Register Company"}
                      </Button>
                    </Form>
                  )}
                </Formik>

                {/* Footer Link */}
                <div className="mt-4 text-center">
                  <button
                    onClick={() => navigate("/")}
                    className="text-primary hover:underline text-sm font-medium"
                  >
                    Go to Home
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
